import React, { useState, useEffect, FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import CsvImportModal from '../classes/CsvImportModal';
import TabContainer from '../layouts/TabContainer';
import Badge from '../ui/Badge';
import Card from '../ui/Card';
import PageContainer from '../layouts/PageContainer';
import { dataService } from '../../services/dataService';
import { AuthContext } from '../../App';
import { Class, Teacher, School, District, UserRole } from '../../data/types';

// Component for displaying and managing classes
const ClassesScreen: FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('All Grades');
  const [schoolFilter, setSchoolFilter] = useState('All Schools');
  const [districtFilter, setDistrictFilter] = useState('All Districts');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [schoolYearFilter, setSchoolYearFilter] = useState('All Years');
  const [showInactiveClasses, setShowInactiveClasses] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [showImportModal, setShowImportModal] = useState(false);
  const [classes, setClasses] = useState<Class[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const { activeRole } = useContext(AuthContext);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesData, teachersData, schoolsData, districtsData] = await Promise.all([
          dataService.getClasses(),
          dataService.getTeachers(),
          dataService.getSchools(),
          dataService.getDistricts()
        ]);

        setClasses(classesData);
        setTeachers(teachersData);
        setSchools(schoolsData);
        setDistricts(districtsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Extract unique values for filters
  const uniqueGrades = Array.from(new Set(classes.map(cls => cls.grade)))
    .sort((a, b) => {
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      return parseInt(a) - parseInt(b);
    });
    
  const uniqueSchoolYears = Array.from(new Set(classes.map(cls => cls.schoolYear))).sort();
  
  const uniqueStatuses = Array.from(new Set(classes.map(cls => cls.status))) as ('active' | 'inactive' | 'archived')[];
  
  // Handle tab change for all roles
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setShowInactiveClasses(tabId === 'inactive');
  };




  // Filter classes based on all filters
  const filteredClasses = classes.filter(cls => {
    // Filter by active/inactive status based on tab
    if (showInactiveClasses && cls.status !== 'inactive') return false;
    if (!showInactiveClasses && cls.status === 'inactive') return false;

    // Apply search query filter
    if (
      searchQuery &&
      !cls.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !cls.grade.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !getTeacherNames(cls.teacherId, cls.teachers).toLowerCase().includes(searchQuery.toLowerCase()) &&
      !getSchoolName(cls.schoolId).toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    // Apply grade filter
    if (gradeFilter !== 'All Grades' && cls.grade !== gradeFilter) {
      return false;
    }

    // Apply school filter
    if (schoolFilter !== 'All Schools' && cls.schoolId.toString() !== schoolFilter) {
      return false;
    }

    // Apply district filter
    if (districtFilter !== 'All Districts') {
      const school = schools.find(s => s.id === cls.schoolId);
      if (!school || school.district.toString() !== districtFilter) {
        return false;
      }
    }

    // Apply status filter
    if (statusFilter !== 'All Statuses' && cls.status !== statusFilter) {
      return false;
    }

    // Apply school year filter
    if (schoolYearFilter !== 'All Years' && cls.schoolYear !== schoolYearFilter) {
      return false;
    }

    return true;
  });

  // Sort classes by name
  const sortedClasses = [...filteredClasses].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Helper functions for displaying related data
  const getTeacherName = (teacherId: string | number): string => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unknown Teacher';
  };

  const getTeacherNames = (teacherId: string | number, additionalTeachers?: (string | number)[]): string => {
    const allTeachers = [teacherId, ...(additionalTeachers || [])];
    if (allTeachers.length === 0) return 'No Teacher Assigned';
    return allTeachers.map(id => getTeacherName(id)).join(', ');
  };

  const getSchoolName = (schoolId: string | number): string => {
    const school = schools.find(s => s.id === schoolId);
    return school ? school.name : 'Unknown School';
  };

  const getDistrictName = (districtId: string | number): string => {
    const district = districts.find(d => d.id === districtId);
    return district ? district.name : 'Unknown District';
  };

  // Handle class click to navigate to class details
  const handleClassClick = (classId: string | number) => {
    navigate(`/classes/${classId}`);
  };

  // Handle CSV import
  const handleImportCSV = async (file: File): Promise<any> => {
    console.log('Importing CSV data:', file);
    // Implement CSV import logic here
    return { success: true, message: 'Import successful' };
  };

  // Get badge variant based on status
  const getStatusBadgeVariant = (status: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'secondary';
      case 'archived':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Render filter content for TabContainer
  const renderFilterContent = () => (
    <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4 mb-4">
      {/* Search Input */}
      <div className="flex-1">
        <input
          type="text"
          placeholder="Search classes..."
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Grade Filter */}
      <div className="w-full md:w-48">
        <select
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value)}
        >
          <option value="All Grades">All Grades</option>
          {uniqueGrades.map(grade => (
            <option key={grade} value={grade}>
              Grade {grade}
            </option>
          ))}
        </select>
      </div>

      {/* School Year Filter */}
      <div className="w-full md:w-48">
        <select
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={schoolYearFilter}
          onChange={(e) => setSchoolYearFilter(e.target.value)}
        >
          <option value="All Years">All Years</option>
          {uniqueSchoolYears.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Status Filter */}
      <div className="w-full md:w-48">
        <select
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All Statuses">All Statuses</option>
          {uniqueStatuses.map(status => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {/* School Filter */}
      <div className="w-full md:w-48">
        <select
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={schoolFilter}
          onChange={(e) => setSchoolFilter(e.target.value)}
        >
          <option value="All Schools">All Schools</option>
          {schools.map(school => (
            <option key={school.id} value={school.id.toString()}>
              {school.name}
            </option>
          ))}
        </select>
      </div>

      {/* District Filter */}
      <div className="w-full md:w-48">
        <select
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={districtFilter}
          onChange={(e) => setDistrictFilter(e.target.value)}
        >
          <option value="All Districts">All Districts</option>
          {districts.map(district => (
            <option key={district.id} value={district.id.toString()}>
              {district.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  // Render class list as cards for teacher role
  const renderClassCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {sortedClasses.map(cls => (
        <Card
          key={cls.id}
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleClassClick(cls.id)}
        >
          <div className="p-4">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-semibold">{cls.name}</h3>
              <Badge variant={getStatusBadgeVariant(cls.status)}>{cls.status}</Badge>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              <span className="font-medium">Grade:</span> {cls.grade}
            </div>
            {activeRole !== 'Teacher' && (
              <>
                <div className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Teacher:</span> {getTeacherNames(cls.teacherId, cls.teachers)}
                </div>
                <div className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">School:</span> {getSchoolName(cls.schoolId)}
                </div>
                <div className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">District:</span> {getDistrictName(schools.find(s => s.id === cls.schoolId)?.district || 0)}
                </div>
              </>
            )}
            <div className="text-sm text-gray-600">
              <span className="font-medium">School Year:</span> {cls.schoolYear}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  // Render class list as table for superadmin and schooladmin roles
  const renderClassTable = () => {
    // Check if we should show the district column (only for SuperAdmin)
    const showDistrictColumn = activeRole === 'SuperAdmin';
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teacher</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
              {showDistrictColumn && <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">District</th>}
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School Year</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedClasses.map(cls => (
              <tr 
                key={cls.id} 
                className="hover:bg-gray-50"
              >
                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{cls.name}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{cls.grade}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{getTeacherNames(cls.teacherId, cls.teachers)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{getSchoolName(cls.schoolId)}</td>
                {showDistrictColumn && (
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    {getDistrictName(schools.find(s => s.id === cls.schoolId)?.district || 0)}
                  </td>
                )}
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{cls.students?.length || 0}</td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{cls.schoolYear}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge variant={getStatusBadgeVariant(cls.status)}>{cls.status}</Badge>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClassClick(cls.id);
                    }} 
                    className="text-primary hover:text-primary-dark font-medium"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <PageContainer title="Classes" userRole="Teacher">

      {/* Buttons removed as requested */}

      <TabContainer
        tabs={[
          { id: 'active', label: 'Active Classes' },
          { id: 'inactive', label: 'Inactive Classes' }
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        filterContent={renderFilterContent()}
      >
        {sortedClasses.length > 0 ? (
          (() => {
            const shouldShowTable = ['SuperAdmin', 'SchoolAdmin'].includes(activeRole || '');
            return shouldShowTable ? renderClassTable() : renderClassCards();
          })()
        ) : (
          <div className="text-center py-10">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No classes found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || districtFilter !== 'All Districts' || schoolFilter !== 'All Schools' ||
                gradeFilter !== 'All Grades' || statusFilter !== 'All Statuses' || schoolYearFilter !== 'All Years'
                ? 'Try adjusting your filters.'
                : 'Import or create a new class to get started.'}
            </p>
          </div>
        )}
      </TabContainer>

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportCSV}
        entityName="Classes"
      />
    </PageContainer>
  );
};

export default ClassesScreen;
